/**
 * MCPPickerDialog — modal flotante de selección de servidores MCP (CHG-AGT-002).
 *
 * Sustituye el picker inline del editor de agentes por un overlay ADR-114 con
 * tabla Nombre · Estado · Salud y búsqueda en vivo en servidor (param `search`
 * del endpoint `GET /api/v1/mcp/servers`, debounce ~300 ms y paginación por
 * `total`).
 *
 * Decisiones cerradas: solo servidores `active`; el modal permanece abierto al
 * añadir; los ya enlazados se excluyen de la lista. No introduce contrato
 * nuevo: consume el hook existente `useMCPServers`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@/i18n';
import {
  Button,
  Icon,
  SearchInput,
  Spinner,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableEmpty,
  AppModal,
} from '@/components/ui';
import { Wrench } from 'lucide-react';
import { useMCPServers } from '../hooks/useMCPServers';
import type { MCPHealthStatus, MCPServerStatus, MCPServerSummary } from '../types';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

function healthStatusType(health: MCPHealthStatus) {
  switch (health) {
    case 'healthy':
      return 'healthy' as const;
    case 'degraded':
      return 'warning' as const;
    case 'unhealthy':
      return 'error' as const;
    default:
      return 'default' as const;
  }
}

function serverStatusType(status: MCPServerStatus) {
  switch (status) {
    case 'active':
      return 'active' as const;
    case 'disabled':
      return 'draft' as const;
    case 'error':
      return 'error' as const;
    default:
      return 'default' as const;
  }
}

export interface MCPPickerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Servidores ya enlazados al agente (se excluyen de la lista). */
  isBound: (server: MCPServerSummary) => boolean;
  /** Añade el servidor al agente; el modal permanece abierto. */
  onAdd: (server: MCPServerSummary) => void;
}

export function MCPPickerDialog({
  open,
  onClose,
  isBound,
  onAdd,
}: MCPPickerDialogProps) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, error } = useMCPServers({
    page,
    pageSize: PAGE_SIZE,
    status: 'active',
    search: debouncedSearch || undefined,
  });

  const servers = useMemo(() => {
    const rows = Array.isArray(data) ? data : (data?.data ?? []);
    return rows.filter((server) => !isBound(server));
  }, [data, isBound]);

  const total = Array.isArray(data) ? data.length : (data?.total ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasSearch = debouncedSearch.length > 0;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      size="lg"
      ariaLabel={t('admin.agents.editor.tools.pickerTitle')}
      testId="mcp-picker-dialog"
    >
      <div className="flex max-h-[min(85dvh,900px)] flex-col overflow-hidden rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
          <div className="flex items-start gap-2">
            <Wrench className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <h2 className="text-lg font-semibold">
                {t('admin.agents.editor.tools.pickerTitle')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('admin.agents.editor.tools.pickerDescription')}
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <Icon name="x" className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="border-b px-5 py-3">
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
            placeholder={t('admin.agents.editor.tools.pickerSearchPlaceholder')}
            aria-label={t('admin.agents.editor.tools.pickerSearchPlaceholder')}
            autoFocus
          />
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12" data-testid="mcp-picker-loading">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="px-5 py-8 text-center text-sm text-destructive" data-testid="mcp-picker-error">
              {t('admin.agents.editor.tools.pickerError')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.name')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead>{t('admin.integrations.health')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {servers.length === 0 ? (
                  <TableEmpty
                    colSpan={3}
                    message={
                      hasSearch
                        ? t('admin.agents.editor.tools.pickerNoResults')
                        : t('admin.agents.editor.tools.pickerEmpty')
                    }
                  />
                ) : (
                  servers.map((server) => (
                    <TableRow
                      key={server.id}
                      className="cursor-pointer hover:bg-muted/50"
                      data-testid={`mcp-picker-row-${server.id}`}
                      onClick={() => onAdd(server)}
                    >
                      <TableCell>
                        <button
                          type="button"
                          className="text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label={t('admin.agents.editor.tools.pickerAdd', {
                            name: server.name,
                          })}
                          onClick={(event) => {
                            event.stopPropagation();
                            onAdd(server);
                          }}
                        >
                          {server.name}
                        </button>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={serverStatusType(server.status)}
                          label={t(
                            `admin.agents.editor.tools.serverStatus.${server.status}`,
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={healthStatusType(server.health_status)}
                          label={t(
                            `admin.agents.editor.tools.healthStatus.${server.health_status}`,
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
          <span className="text-sm text-muted-foreground">
            {totalPages > 1
              ? t('admin.agents.editor.tools.pickerPage', {
                  page,
                  total: totalPages,
                })
              : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || isLoading}
            >
              {t('common.previous')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
              disabled={page >= totalPages || isLoading}
            >
              {t('common.next')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </div>
    </AppModal>
  );
}
